import React, { useEffect, useMemo, useState } from 'react';
import { useSupabase } from '../../../providers/SupabaseProvider';
import { useAuth } from '../../../providers/SupabaseProvider';
import {
  Loader2,
  X,
  Clock,
  CalendarClock,
  AlertTriangle,
  User,
  MessageSquare,
  Phone,
  Mail,
  MessageCircle,
} from 'lucide-react';

interface AppointmentModalProps {
  show: boolean;
  onClose: () => void;
  sampleId: string;
  onAppointmentScheduled: () => void;
}

interface OrganizationUserOption {
  user_id: string;
  full_name?: string | null;
  email?: string | null;
}

interface CustomerPhone {
  id: string;
  phone_number: string;
  type: string;
}

interface WhatsAppTemplateOption {
  id: string;
  internal_name: string;
  content: string;
}

interface SampleLoadRow {
  assigned_user_id?: string | null;
  customer_id?: string | null;
  assigned_appointment_date?: string | null;
  assigned_appointment_time?: string | null;
  assigned_appointment_duration_minutes?: number | null;
  email_order_enabled?: boolean | null;
  email_order_email?: string | null;
  email_order_token?: string | null;
  email_order_sent_at?: string | null;
  email_order_confirmed_at?: string | null;
  email_order_subject?: string | null;
  email_order_message?: string | null;
  sms_order_enabled?: boolean | null;
  sms_order_phone_number?: string | null;
  whatsapp_order_enabled?: boolean | null;
  whatsapp_order_phone_number?: string | null;
  whatsapp_order_token?: string | null;
  whatsapp_order_template_id?: string | null;
  whatsapp_order_template_name?: string | null;
  whatsapp_order_message?: string | null;
}

type BusinessHoursDay = {
  open: boolean;
  start: string;
  end: string;
};

type BusinessHoursState = {
  monday: BusinessHoursDay;
  tuesday: BusinessHoursDay;
  wednesday: BusinessHoursDay;
  thursday: BusinessHoursDay;
  friday: BusinessHoursDay;
  saturday: BusinessHoursDay;
  sunday: BusinessHoursDay;
};

const DEFAULT_BUSINESS_HOURS: BusinessHoursState = {
  monday: { open: true, start: '09:00', end: '17:00' },
  tuesday: { open: true, start: '09:00', end: '17:00' },
  wednesday: { open: true, start: '09:00', end: '17:00' },
  thursday: { open: true, start: '09:00', end: '17:00' },
  friday: { open: true, start: '09:00', end: '17:00' },
  saturday: { open: false, start: '09:00', end: '17:00' },
  sunday: { open: false, start: '09:00', end: '17:00' },
};

const WEEKDAY_KEYS: (keyof BusinessHoursState)[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const APPOINTMENT_CONFIRMATION_PLACEHOLDER = '{{appointment_confirmation_link}}';
const DEFAULT_SMS_TEMPLATE =
  'Einbestellung von {{organization_name}}: Bitte bestätigen Sie Ihren Termin am {{appointment_date}} über {{appointment_confirmation_link}}.';
const DEFAULT_EMAIL_ORDER_SUBJECT = 'Einbestellung von {{organization_name}}';
const DEFAULT_EMAIL_ORDER_BODY =
  'Sehr geehrte(r) {{customer_name}},\n\nbitte bestätigen Sie Ihren Termin am {{appointment_date}} über {{appointment_confirmation_link}}.\n\nMit freundlichen Grüßen\n{{organization_name}}';
// Standarddauer für Termine im Einbestellungsprozess.
const DEFAULT_DURATION_MINUTES = 15;
const EMAIL_ORDER_TEMPLATE_TYPE = 'sample_order_email';
type OrderChannel = 'phone' | 'email' | 'whatsapp' | 'sms';
const WHATSAPP_ORDER_CHANNEL_TEMPORARILY_DISABLED = true;

const AppointmentModal: React.FC<AppointmentModalProps> = ({
  show,
  onClose,
  sampleId,
  onAppointmentScheduled,
}) => {
  const supabase = useSupabase();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [organizationId, setOrganizationId] = useState<string>('');
  const [organizationSlug, setOrganizationSlug] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string>('');
  const [organizationAddress, setOrganizationAddress] = useState<string>('');
  const [businessHours, setBusinessHours] = useState<BusinessHoursState>(DEFAULT_BUSINESS_HOURS);

  const [organizationUsers, setOrganizationUsers] = useState<OrganizationUserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState<string>('');

  const [customerName, setCustomerName] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [customerWantsEmail, setCustomerWantsEmail] = useState(false);
  const [customerWantsSms, setCustomerWantsSms] = useState(false);
  const [customerWantsWhatsapp, setCustomerWantsWhatsapp] = useState(false);
  const [emailChannelEnabled, setEmailChannelEnabled] = useState(false);
  const [smsChannelEnabled, setSmsChannelEnabled] = useState(false);
  const [whatsappChannelEnabled, setWhatsappChannelEnabled] = useState(false);
  const [customerPhones, setCustomerPhones] = useState<CustomerPhone[]>([]);
  const [selectedSmsPhoneNumber, setSelectedSmsPhoneNumber] = useState('');
  const [selectedWhatsappPhoneNumber, setSelectedWhatsappPhoneNumber] = useState('');
  const [smsTemplateContent, setSmsTemplateContent] = useState(DEFAULT_SMS_TEMPLATE);
  const [emailTemplateSubject, setEmailTemplateSubject] = useState(DEFAULT_EMAIL_ORDER_SUBJECT);
  const [emailTemplateContent, setEmailTemplateContent] = useState(DEFAULT_EMAIL_ORDER_BODY);
  const [emailTemplateHtmlContent, setEmailTemplateHtmlContent] = useState('');
  const [emailTemplateSignature, setEmailTemplateSignature] = useState('');
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplateOption[]>([]);
  const [selectedWhatsappTemplateId, setSelectedWhatsappTemplateId] = useState('');
  const [orderChannel, setOrderChannel] = useState<OrderChannel>('phone');
  const [appointmentDate, setAppointmentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [appointmentTime, setAppointmentTime] = useState<string>(new Date().toTimeString().slice(0, 5));
  const [assignedAppointmentDate, setAssignedAppointmentDate] = useState<string>(
    new Date().toISOString().split('T')[0],
  );
  const [assignedAppointmentTime, setAssignedAppointmentTime] = useState<string>(
    new Date().toTimeString().slice(0, 5),
  );
  const [assignedAppointmentDurationMinutes, setAssignedAppointmentDurationMinutes] = useState<string>(
    String(DEFAULT_DURATION_MINUTES),
  );
  const [busyIntervals, setBusyIntervals] = useState<Array<{ startMinutes: number; endMinutes: number }>>([]);
  const [loadingBusyIntervals, setLoadingBusyIntervals] = useState(false);

  const customerHasEmail = customerEmail.trim().length > 0;
  // E-Mail-Einbestellung bleibt nur dann aktiv, wenn Workflow, Kontaktwunsch und eine Adresse zusammen erfüllt sind.
  const emailOrderAvailable = emailChannelEnabled && customerWantsEmail && customerHasEmail;
  const smsOrderAvailable = smsChannelEnabled && customerWantsSms && customerPhones.length > 0;
  const isSmsOrder = orderChannel === 'sms';
  const isPhoneOrder = orderChannel === 'phone';
  const isNonPhoneOrder = !isPhoneOrder;
  const isEmailOrder = orderChannel === 'email';
  const isWhatsappOrder = orderChannel === 'whatsapp';
  const whatsappOptionVisible = whatsappChannelEnabled && customerWantsWhatsapp;
  const whatsappHasPhoneNumbers = customerPhones.length > 0;
  const whatsappHasTemplates = whatsappTemplates.length > 0;
  const whatsappOrderAvailable = whatsappOptionVisible && whatsappHasPhoneNumbers && whatsappHasTemplates;
  const requiresManualAppointmentSlot = orderChannel === 'phone';
  const selectedOrderChannelLabel =
    orderChannel === 'phone'
      ? 'Telefon'
      : orderChannel === 'email'
        ? 'E-Mail'
        : orderChannel === 'whatsapp'
          ? 'WhatsApp'
          : 'SMS';

  const selectedWhatsappTemplate = useMemo(
    () => whatsappTemplates.find((template) => template.id === selectedWhatsappTemplateId) || null,
    [selectedWhatsappTemplateId, whatsappTemplates],
  );

  const emailTemplateHint = useMemo(() => {
    if (!emailChannelEnabled) {
      return 'E-Mail-Einbestellung ist in den Workflow-Einstellungen deaktiviert.';
    }
    if (!customerWantsEmail) {
      return 'Für diesen Kunden ist kein E-Mail-Kontaktwunsch hinterlegt.';
    }
    if (!customerHasEmail) {
      return 'Für diesen Kunden ist keine E-Mail-Adresse hinterlegt.';
    }
    if (!emailTemplateContent.trim()) {
      return 'Es ist keine E-Mail-Vorlage für die Einbestellung hinterlegt.';
    }
    return null;
  }, [customerHasEmail, customerWantsEmail, emailChannelEnabled, emailTemplateContent]);

  const whatsappOptionHint = useMemo(() => {
    if (WHATSAPP_ORDER_CHANNEL_TEMPORARILY_DISABLED) {
      return 'WhatsApp-Einbestellung ist vorübergehend global deaktiviert.';
    }
    if (!whatsappChannelEnabled) {
      return 'WhatsApp-Einbestellung ist in den Workflow-Einstellungen deaktiviert.';
    }
    if (!customerWantsWhatsapp) {
      return 'Für diesen Kunden ist kein WhatsApp-Kontaktwunsch hinterlegt.';
    }
    if (!whatsappHasPhoneNumbers) {
      return 'Für diesen Kunden ist keine Telefonnummer für WhatsApp hinterlegt.';
    }
    if (!whatsappHasTemplates) {
      return 'Für die Organisation ist keine WhatsApp-Vorlage hinterlegt.';
    }
    return null;
  }, [customerWantsWhatsapp, whatsappChannelEnabled, whatsappHasPhoneNumbers, whatsappHasTemplates]);

  const normalizeContactMethods = (rawValue: string | null | undefined): string[] =>
    (rawValue || '')
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);

  const buildOrganizationAddress = (organization: {
    street?: string | null;
    house_number?: string | null;
    postal_code?: string | null;
    city?: string | null;
  }): string => {
    const firstLine = [organization.street, organization.house_number].filter(Boolean).join(' ').trim();
    const secondLine = [organization.postal_code, organization.city].filter(Boolean).join(' ').trim();
    return [firstLine, secondLine].filter(Boolean).join(', ').trim();
  };

  const applyTemplatePlaceholders = (template: string, values: Record<string, string>): string => {
    let renderedTemplate = template;
    Object.entries(values).forEach(([key, value]) => {
      renderedTemplate = renderedTemplate.split(`{{${key}}}`).join(value);
    });
    return renderedTemplate.trim();
  };

  const escapeHtml = (value: string): string =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  // Fallback, falls keine HTML-Vorlage gepflegt ist: Plaintext sicher in einfache HTML-Struktur umwandeln.
  const convertPlainTextToHtml = (text: string): string =>
    text
      .split('\n')
      .map((line) => (line.trim().length > 0 ? escapeHtml(line) : '<br />'))
      .join('<br />')
      .trim();

  const formatAppointmentDateForTemplate = (dateValue: string): string => {
    if (!dateValue) return '';
    const parsedDate = new Date(`${dateValue}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) {
      return dateValue;
    }
    return parsedDate.toLocaleDateString('de-DE');
  };

  const normalizeGermanSmsPhoneNumber = (rawPhoneNumber: string): string => {
    const trimmed = rawPhoneNumber.trim();
    if (!trimmed) return '';

    const compact = trimmed.replace(/[\s\-()./]/g, '');
    if (!compact) return '';
    if (compact.startsWith('+')) return compact;
    if (compact.startsWith('00')) return `+${compact.slice(2)}`;
    if (compact.startsWith('0')) return `+49${compact.slice(1)}`;
    return compact;
  };

  const normalizeTimeForInput = (timeValue: string | null | undefined): string => {
    if (!timeValue) return new Date().toTimeString().slice(0, 5);
    const [hours, minutes] = timeValue.split(':');
    if (!hours || !minutes) return new Date().toTimeString().slice(0, 5);
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  const normalizeBusinessHours = (rawValue: unknown): BusinessHoursState => {
    if (!rawValue || typeof rawValue !== 'object') {
      return DEFAULT_BUSINESS_HOURS;
    }

    const source = rawValue as Partial<Record<keyof BusinessHoursState, Partial<BusinessHoursDay>>>;
    const normalized = { ...DEFAULT_BUSINESS_HOURS };

    (Object.keys(DEFAULT_BUSINESS_HOURS) as (keyof BusinessHoursState)[]).forEach((dayKey) => {
      const dayFromSource = source[dayKey];
      if (!dayFromSource || typeof dayFromSource !== 'object') {
        return;
      }

      normalized[dayKey] = {
        open: typeof dayFromSource.open === 'boolean'
          ? dayFromSource.open
          : DEFAULT_BUSINESS_HOURS[dayKey].open,
        start: typeof dayFromSource.start === 'string' && dayFromSource.start.trim().length > 0
          ? dayFromSource.start
          : DEFAULT_BUSINESS_HOURS[dayKey].start,
        end: typeof dayFromSource.end === 'string' && dayFromSource.end.trim().length > 0
          ? dayFromSource.end
          : DEFAULT_BUSINESS_HOURS[dayKey].end,
      };
    });

    return normalized;
  };

  const parseTimeToMinutes = (timeValue: string): number | null => {
    const [hoursRaw, minutesRaw] = timeValue.split(':');
    const hours = Number.parseInt(hoursRaw ?? '', 10);
    const minutes = Number.parseInt(minutesRaw ?? '', 10);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return (hours * 60) + minutes;
  };

  const formatMinutesToTime = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const openingHoursForSelectedDate = useMemo(() => {
    if (!assignedAppointmentDate) return null;
    const selectedDate = new Date(`${assignedAppointmentDate}T00:00:00`);
    if (Number.isNaN(selectedDate.getTime())) return null;
    const dayKey = WEEKDAY_KEYS[selectedDate.getDay()];
    return businessHours[dayKey] || null;
  }, [assignedAppointmentDate, businessHours]);

  const appointmentDurationForSuggestions = useMemo(() => {
    const parsed = Number.parseInt(assignedAppointmentDurationMinutes, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_DURATION_MINUTES;
  }, [assignedAppointmentDurationMinutes]);

  const phoneTimeSuggestions = useMemo(() => {
    if (!openingHoursForSelectedDate?.open || !assignedUserId) return [];

    const startMinutes = parseTimeToMinutes(openingHoursForSelectedDate.start);
    const endMinutes = parseTimeToMinutes(openingHoursForSelectedDate.end);
    if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) return [];

    const slots: string[] = [];
    for (let current = startMinutes; current + appointmentDurationForSuggestions <= endMinutes; current += 15) {
      const candidateEndMinutes = current + appointmentDurationForSuggestions;
      const overlapsBusyInterval = busyIntervals.some((busyInterval) =>
        current < busyInterval.endMinutes && candidateEndMinutes > busyInterval.startMinutes,
      );

      if (!overlapsBusyInterval) {
        slots.push(formatMinutesToTime(current));
      }
    }
    return slots;
  }, [appointmentDurationForSuggestions, assignedUserId, busyIntervals, openingHoursForSelectedDate]);

  const nonPhoneAvailabilityInfo = useMemo(() => {
    if (!isNonPhoneOrder) return null;
    if (!assignedUserId) {
      return {
        className: 'text-xs text-amber-700 dark:text-amber-300',
        text: 'Bitte zuerst eine Benutzerzuweisung auswählen, um freie Termine prüfen zu können.',
      };
    }
    if (loadingBusyIntervals) {
      return {
        className: 'text-xs text-gray-500 dark:text-gray-400',
        text: 'Freie Termine am gewählten Tag werden geladen.',
      };
    }
    if (!openingHoursForSelectedDate?.open) {
      return {
        className: 'text-xs text-amber-700 dark:text-amber-300',
        text: 'Für den gewählten Tag sind laut Öffnungszeiten keine Termine verfügbar.',
      };
    }
    if (phoneTimeSuggestions.length === 0) {
      return {
        className: 'text-xs text-amber-700 dark:text-amber-300',
        text: 'Für den gewählten Tag sind keine freien Termine mehr verfügbar.',
      };
    }
    // Für Nicht-Telefonkanäle wird dieselbe Slot-Berechnung genutzt wie bei Telefon.
    return {
      className: 'text-xs text-emerald-700 dark:text-emerald-300',
      text: `Für den gewählten Tag sind noch ${phoneTimeSuggestions.length} freie Termine verfügbar.`,
    };
  }, [assignedUserId, isNonPhoneOrder, loadingBusyIntervals, openingHoursForSelectedDate, phoneTimeSuggestions.length]);

  const parseFunctionErrorResponse = async (response: Response, fallbackMessage: string): Promise<string> => {
    const responseText = await response.text();
    if (!responseText) {
      return fallbackMessage;
    }

    try {
      const parsedResponse = JSON.parse(responseText) as {
        error?: string;
        message?: string;
        detail?: string;
        details?: string;
      };
      const normalizedError = parsedResponse.error || parsedResponse.message || parsedResponse.detail || parsedResponse.details || responseText;
      const normalizedDetails = parsedResponse.details || parsedResponse.detail;
      if (
        normalizedError === 'Unauthorized'
        && typeof normalizedDetails === 'string'
        && normalizedDetails.trim()
      ) {
        return `${normalizedError}: ${normalizedDetails.trim()}`;
      }
      return normalizedError;
    } catch {
      return responseText;
    }
  };

  const parseWebhookErrorResponse = async (response: Response, fallbackMessage: string): Promise<string> => {
    const responseText = await response.text();
    if (!responseText) {
      return fallbackMessage;
    }

    try {
      const parsedResponse = JSON.parse(responseText) as {
        error?: string;
        message?: string;
        detail?: string;
        details?: string;
      };
      return parsedResponse.error
        || parsedResponse.message
        || parsedResponse.detail
        || parsedResponse.details
        || responseText;
    } catch {
      return responseText;
    }
  };

  const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
    const tokenParts = token.split('.');
    if (tokenParts.length < 2) return null;

    try {
      const base64Payload = tokenParts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(tokenParts[1].length / 4) * 4, '=');
      const payloadJson = atob(base64Payload);
      return JSON.parse(payloadJson) as Record<string, unknown>;
    } catch {
      return null;
    }
  };

  const getSupabaseFunctionUrl = (functionName: string): string => {
    const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL ?? '').trim().replace(/\/+$/, '');
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL ist nicht konfiguriert.');
    }

    return `${supabaseUrl}/functions/v1/${functionName}`;
  };

  const buildFunctionInvokeHeaders = async (): Promise<Record<string, string>> => {
    const nowInSeconds = Math.floor(Date.now() / 1000);
    const {
      data: { session: currentSession },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError) {
      throw new Error('Sitzung konnte nicht geprüft werden. Bitte erneut anmelden.');
    }

    let resolvedSession = currentSession;

    // Access-Token kurz vor Ablauf werden aktiv erneuert, damit Edge-Functions kein 401 durch abgelaufene JWTs liefern.
    if (
      resolvedSession?.refresh_token
      && resolvedSession.expires_at
      && resolvedSession.expires_at <= nowInSeconds + 60
    ) {
      const { data: refreshedData, error: refreshError } = await supabase.auth.refreshSession({
        refresh_token: resolvedSession.refresh_token,
      });

      if (refreshError) {
        throw new Error('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
      }

      resolvedSession = refreshedData.session;
    }

    if (!resolvedSession?.access_token) {
      throw new Error('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
    }

    const decodedAccessToken = decodeJwtPayload(resolvedSession.access_token);
    const tokenRole = typeof decodedAccessToken?.role === 'string' ? decodedAccessToken.role : '';
    if (tokenRole !== 'authenticated') {
      throw new Error('Sitzung ungültig. Bitte melden Sie sich erneut an.');
    }

    return {
      Authorization: `Bearer ${resolvedSession.access_token}`,
    };
  };

  const callProtectedEdgeFunction = async <TResponse extends Record<string, unknown>>(
    functionName: string,
    payload: Record<string, unknown>,
    fallbackErrorMessage: string,
  ): Promise<TResponse | null> => {
    const supabaseAnonKey = String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? '').trim();
    if (!supabaseAnonKey) {
      throw new Error('VITE_SUPABASE_ANON_KEY ist nicht konfiguriert.');
    }

    const authHeaders = await buildFunctionInvokeHeaders();
    const functionUrl = getSupabaseFunctionUrl(functionName);

    let functionResponse: Response;
    try {
      functionResponse = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseAnonKey,
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });
    } catch {
      throw new Error(fallbackErrorMessage);
    }

    if (!functionResponse.ok) {
      const responseMessage = await parseFunctionErrorResponse(functionResponse, fallbackErrorMessage);
      throw new Error(responseMessage || fallbackErrorMessage);
    }

    const responseText = await functionResponse.text();
    if (!responseText) {
      return null;
    }

    try {
      return JSON.parse(responseText) as TResponse;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    async function loadAssignmentOptions() {
      if (!show || !sampleId || !user?.id) {
        return;
      }

      try {
        setLoadingUsers(true);
        const now = new Date();
        const nowDate = now.toISOString().split('T')[0];
        const nowTime = now.toTimeString().slice(0, 5);
        // Neue Einbestellung startet mit einem sauberen Standardzustand.
        setOrderChannel('phone');
        setAppointmentDate(nowDate);
        setAppointmentTime(nowTime);
        setAssignedAppointmentDate(nowDate);
        setAssignedAppointmentTime(nowTime);
        setCustomerEmail('');
        setCustomerWantsEmail(false);
        setEmailChannelEnabled(false);
        setCustomerWantsWhatsapp(false);
        setWhatsappChannelEnabled(false);
        setSelectedWhatsappPhoneNumber('');
        setEmailTemplateSubject(DEFAULT_EMAIL_ORDER_SUBJECT);
        setEmailTemplateContent(DEFAULT_EMAIL_ORDER_BODY);
        setEmailTemplateHtmlContent('');
        setEmailTemplateSignature('');
        setWhatsappTemplates([]);
        setSelectedWhatsappTemplateId('');

        let sampleData: SampleLoadRow | null = null;
        const sampleExtendedResult = await supabase
          .from('customer_samples')
          .select(
            'assigned_user_id, customer_id, assigned_appointment_date, assigned_appointment_time, assigned_appointment_duration_minutes, email_order_enabled, email_order_email, email_order_token, email_order_sent_at, email_order_confirmed_at, email_order_subject, email_order_message, sms_order_enabled, sms_order_phone_number, whatsapp_order_enabled, whatsapp_order_phone_number, whatsapp_order_token, whatsapp_order_template_id, whatsapp_order_template_name, whatsapp_order_message',
          )
          .eq('id', sampleId)
          .maybeSingle();

        if (sampleExtendedResult.error) {
          const errorMessage = String(sampleExtendedResult.error.message || '');
          const missingExtendedColumns =
            errorMessage.includes('assigned_appointment_date')
            || errorMessage.includes('assigned_appointment_time')
            || errorMessage.includes('assigned_appointment_duration_minutes')
            || errorMessage.includes('email_order_enabled')
            || errorMessage.includes('email_order_email')
            || errorMessage.includes('email_order_token')
            || errorMessage.includes('email_order_sent_at')
            || errorMessage.includes('email_order_confirmed_at')
            || errorMessage.includes('email_order_subject')
            || errorMessage.includes('email_order_message')
            || errorMessage.includes('sms_order_enabled')
            || errorMessage.includes('sms_order_phone_number')
            || errorMessage.includes('whatsapp_order_enabled')
            || errorMessage.includes('whatsapp_order_phone_number')
            || errorMessage.includes('whatsapp_order_token')
            || errorMessage.includes('whatsapp_order_template_id')
            || errorMessage.includes('whatsapp_order_template_name')
            || errorMessage.includes('whatsapp_order_message');

          if (!missingExtendedColumns) throw sampleExtendedResult.error;

          const sampleFallbackResult = await supabase
            .from('customer_samples')
            .select('assigned_user_id, customer_id')
            .eq('id', sampleId)
            .maybeSingle();

          if (sampleFallbackResult.error) throw sampleFallbackResult.error;
          sampleData = (sampleFallbackResult.data || null) as SampleLoadRow | null;
        } else {
          sampleData = (sampleExtendedResult.data || null) as SampleLoadRow | null;
        }

        setAssignedUserId(sampleData?.assigned_user_id || '');
        setOrderChannel(
          sampleData?.email_order_enabled
            ? 'email'
            : sampleData?.sms_order_enabled
              ? 'sms'
              : sampleData?.whatsapp_order_enabled
                ? 'whatsapp'
                : 'phone',
        );
        setAssignedAppointmentDate(sampleData?.assigned_appointment_date || new Date().toISOString().split('T')[0]);
        setAssignedAppointmentTime(normalizeTimeForInput(sampleData?.assigned_appointment_time));
        setAssignedAppointmentDurationMinutes(
          sampleData?.assigned_appointment_duration_minutes && Number(sampleData.assigned_appointment_duration_minutes) > 0
            ? String(sampleData.assigned_appointment_duration_minutes)
            : String(DEFAULT_DURATION_MINUTES),
        );

        const { data: orgData, error: orgError } = await supabase
          .from('organization_users')
          .select('organization_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (orgError) throw orgError;

        if (!orgData?.organization_id) {
          setOrganizationId('');
          setOrganizationSlug('');
          setOrganizationName('');
          setOrganizationAddress('');
          setBusinessHours(DEFAULT_BUSINESS_HOURS);
          setEmailChannelEnabled(false);
          setSmsChannelEnabled(false);
          setWhatsappChannelEnabled(false);
          setCustomerName('');
          setCustomerEmail('');
          setCustomerWantsEmail(false);
          setCustomerWantsSms(false);
          setCustomerWantsWhatsapp(false);
          setCustomerPhones([]);
          setSelectedSmsPhoneNumber('');
          setSelectedWhatsappPhoneNumber('');
          setEmailTemplateSubject(DEFAULT_EMAIL_ORDER_SUBJECT);
          setEmailTemplateContent(DEFAULT_EMAIL_ORDER_BODY);
          setEmailTemplateHtmlContent('');
          setEmailTemplateSignature('');
          setWhatsappTemplates([]);
          setSelectedWhatsappTemplateId('');
          setOrganizationUsers([]);
          return;
        }

        setOrganizationId(orgData.organization_id);

        const { data: organizationData, error: organizationError } = await supabase
          .from('organizations')
          .select(
            'slug, name, street, house_number, postal_code, city, business_hours, allow_order_channel_sms, allow_order_channel_email, allow_order_channel_whatsapp',
          )
          .eq('id', orgData.organization_id)
          .maybeSingle();

        if (organizationError) throw organizationError;

        setOrganizationSlug(organizationData?.slug || '');
        setOrganizationName(organizationData?.name || '');
        setOrganizationAddress(buildOrganizationAddress(organizationData || {}));
        setBusinessHours(normalizeBusinessHours(organizationData?.business_hours));
        setEmailChannelEnabled(Boolean(organizationData?.allow_order_channel_email));
        setSmsChannelEnabled(Boolean(organizationData?.allow_order_channel_sms));
        const organizationAllowsWhatsapp = Boolean(organizationData?.allow_order_channel_whatsapp);
        // Temporärer Schutz: selbst bei aktivem Organisations-Flag bleibt der Kanal bis zur Entsperrung global aus.
        setWhatsappChannelEnabled(
          !WHATSAPP_ORDER_CHANNEL_TEMPORARILY_DISABLED && organizationAllowsWhatsapp,
        );

        const { data: membersData, error: membersError } = await supabase
          .from('organization_members')
          .select('user_id, full_name, email')
          .eq('organization_id', orgData.organization_id)
          .order('full_name', { ascending: true });

        if (membersError) throw membersError;
        setOrganizationUsers((membersData || []) as OrganizationUserOption[]);

        if (!sampleData?.customer_id) {
          setCustomerName('');
          setCustomerEmail('');
          setCustomerWantsEmail(false);
          setCustomerWantsSms(false);
          setCustomerWantsWhatsapp(false);
          setCustomerPhones([]);
          setSelectedSmsPhoneNumber('');
          setSelectedWhatsappPhoneNumber('');
          setEmailTemplateSubject(DEFAULT_EMAIL_ORDER_SUBJECT);
          setEmailTemplateContent(DEFAULT_EMAIL_ORDER_BODY);
          setEmailTemplateHtmlContent('');
          setEmailTemplateSignature('');
          setSmsTemplateContent(DEFAULT_SMS_TEMPLATE);
          setWhatsappTemplates([]);
          setSelectedWhatsappTemplateId('');
          return;
        }

        const { data: customerData, error: customerError } = await supabase
          .from('customers')
          .select('first_name, last_name, email, advertising_consent_method')
          .eq('id', sampleData.customer_id)
          .maybeSingle();

        if (customerError) throw customerError;

        const customerDisplayName = [customerData?.first_name, customerData?.last_name].filter(Boolean).join(' ').trim();
        const customerConsentMethods = normalizeContactMethods(customerData?.advertising_consent_method);
        setCustomerName(customerDisplayName);
        setCustomerEmail(customerData?.email?.trim() || '');
        setCustomerWantsEmail(customerConsentMethods.includes('email'));
        setCustomerWantsSms(customerConsentMethods.includes('sms'));
        setCustomerWantsWhatsapp(customerConsentMethods.includes('whatsapp'));

        const { data: customerPhoneData, error: customerPhoneError } = await supabase
          .from('customer_phones')
          .select('id, phone_number, type')
          .eq('customer_id', sampleData.customer_id)
          .order('created_at', { ascending: true });

        if (customerPhoneError) throw customerPhoneError;
        const loadedPhones = (customerPhoneData || []) as CustomerPhone[];
        setCustomerPhones(loadedPhones);

        const preselectedPhone =
          sampleData?.sms_order_phone_number && loadedPhones.some((entry) => entry.phone_number === sampleData.sms_order_phone_number)
            ? sampleData.sms_order_phone_number
            : loadedPhones[0]?.phone_number || '';
        setSelectedSmsPhoneNumber(preselectedPhone);

        const preselectedWhatsappPhone =
          sampleData?.whatsapp_order_phone_number
          && loadedPhones.some((entry) => entry.phone_number === sampleData.whatsapp_order_phone_number)
            ? sampleData.whatsapp_order_phone_number
            : loadedPhones[0]?.phone_number || '';
        setSelectedWhatsappPhoneNumber(preselectedWhatsappPhone);

        const { data: smsTemplateData, error: smsTemplateError } = await supabase
          .from('organization_sms_templates')
          .select('content')
          .eq('organization_id', orgData.organization_id)
          .eq('template_type', 'einbestellung')
          .maybeSingle();

        if (smsTemplateError) {
          const errorMessage = String(smsTemplateError.message || '');
          const missingTemplateSchema = errorMessage.includes('organization_sms_templates');
          if (!missingTemplateSchema) throw smsTemplateError;
        } else {
          setSmsTemplateContent(smsTemplateData?.content?.trim() || DEFAULT_SMS_TEMPLATE);
        }

        const { data: emailTemplateData, error: emailTemplateError } = await supabase
          .from('organization_email_templates')
          .select('subject, body, html_body, signature')
          .eq('organization_id', orgData.organization_id)
          .eq('type', EMAIL_ORDER_TEMPLATE_TYPE)
          .maybeSingle();

        if (emailTemplateError) {
          const errorMessage = String(emailTemplateError.message || '');
          const missingTemplateSchema = errorMessage.includes('organization_email_templates');
          if (!missingTemplateSchema) throw emailTemplateError;
          setEmailTemplateSubject(DEFAULT_EMAIL_ORDER_SUBJECT);
          setEmailTemplateContent(DEFAULT_EMAIL_ORDER_BODY);
          setEmailTemplateHtmlContent('');
          setEmailTemplateSignature('');
        } else {
          setEmailTemplateSubject(emailTemplateData?.subject?.trim() || DEFAULT_EMAIL_ORDER_SUBJECT);
          setEmailTemplateContent(emailTemplateData?.body?.trim() || DEFAULT_EMAIL_ORDER_BODY);
          setEmailTemplateHtmlContent(emailTemplateData?.html_body?.trim() || '');
          setEmailTemplateSignature(emailTemplateData?.signature?.trim() || '');
        }

        const { data: whatsappTemplateData, error: whatsappTemplateError } = await supabase
          .from('organization_whatsapp_templates')
          .select('id, internal_name, content')
          .eq('organization_id', orgData.organization_id)
          .order('internal_name', { ascending: true });

        if (whatsappTemplateError) {
          const errorMessage = String(whatsappTemplateError.message || '');
          const missingTemplateSchema = errorMessage.includes('organization_whatsapp_templates');
          if (!missingTemplateSchema) throw whatsappTemplateError;
          setWhatsappTemplates([]);
          setSelectedWhatsappTemplateId('');
        } else {
          const loadedWhatsappTemplates = (whatsappTemplateData || []) as WhatsAppTemplateOption[];
          setWhatsappTemplates(loadedWhatsappTemplates);
          const hasPersistedTemplate =
            Boolean(sampleData?.whatsapp_order_template_id)
            && loadedWhatsappTemplates.some((template) => template.id === sampleData?.whatsapp_order_template_id);
          setSelectedWhatsappTemplateId(
            hasPersistedTemplate
              ? String(sampleData?.whatsapp_order_template_id)
              : (loadedWhatsappTemplates[0]?.id || ''),
          );
        }
      } catch (loadError) {
        console.error('Error loading assignment users for sample appointment:', loadError);
        setOrganizationId('');
        setOrganizationSlug('');
        setOrganizationName('');
        setOrganizationAddress('');
        setBusinessHours(DEFAULT_BUSINESS_HOURS);
        setEmailChannelEnabled(false);
        setSmsChannelEnabled(false);
        setWhatsappChannelEnabled(false);
        setCustomerName('');
        setCustomerEmail('');
        setCustomerWantsEmail(false);
        setCustomerWantsSms(false);
        setCustomerWantsWhatsapp(false);
        setCustomerPhones([]);
        setSelectedSmsPhoneNumber('');
        setSelectedWhatsappPhoneNumber('');
        setEmailTemplateSubject(DEFAULT_EMAIL_ORDER_SUBJECT);
        setEmailTemplateContent(DEFAULT_EMAIL_ORDER_BODY);
        setEmailTemplateHtmlContent('');
        setEmailTemplateSignature('');
        setWhatsappTemplates([]);
        setSelectedWhatsappTemplateId('');
        setOrganizationUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    }

    loadAssignmentOptions();
  }, [show, sampleId, supabase, user]);

  useEffect(() => {
    if (!smsOrderAvailable && isSmsOrder) {
      setOrderChannel('phone');
    }
  }, [isSmsOrder, smsOrderAvailable]);

  useEffect(() => {
    if (!emailOrderAvailable && isEmailOrder) {
      setOrderChannel('phone');
    }
  }, [emailOrderAvailable, isEmailOrder]);

  useEffect(() => {
    if (!isWhatsappOrder) {
      return;
    }

    if (!whatsappOptionVisible || !whatsappOrderAvailable) {
      setOrderChannel('phone');
    }
  }, [isWhatsappOrder, whatsappOptionVisible, whatsappOrderAvailable]);

  useEffect(() => {
    let cancelled = false;

    const loadBusyIntervalsForAssignedUser = async () => {
      if (!show || !organizationId || !assignedUserId || !assignedAppointmentDate) {
        setBusyIntervals([]);
        setLoadingBusyIntervals(false);
        return;
      }

      const dayStart = new Date(`${assignedAppointmentDate}T00:00:00`);
      if (Number.isNaN(dayStart.getTime())) {
        setBusyIntervals([]);
        setLoadingBusyIntervals(false);
        return;
      }
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);
      const dayStartIso = dayStart.toISOString();
      const dayEndIso = dayEnd.toISOString();

      try {
        setLoadingBusyIntervals(true);

        const baseSelect = 'id, start, end, all_day';
        const extendedSelect = `${baseSelect}, source_type, source_id`;

        const fetchRowsBySelect = async (selectColumns: string) => {
          const [withinDayResult, overlapResult] = await Promise.all([
            supabase
              .from('calendar_events')
              .select(selectColumns)
              .eq('organization_id', organizationId)
              .eq('user_id', assignedUserId)
              .gte('start', dayStartIso)
              .lt('start', dayEndIso),
            supabase
              .from('calendar_events')
              .select(selectColumns)
              .eq('organization_id', organizationId)
              .eq('user_id', assignedUserId)
              .lt('start', dayStartIso)
              .gt('end', dayStartIso),
          ]);

          return { withinDayResult, overlapResult };
        };

        let rowsWithinDay: Array<Record<string, any>> = [];
        let rowsOverlappingDayStart: Array<Record<string, any>> = [];

        const extendedRowsResult = await fetchRowsBySelect(extendedSelect);
        if (extendedRowsResult.withinDayResult.error || extendedRowsResult.overlapResult.error) {
          const primaryError = extendedRowsResult.withinDayResult.error || extendedRowsResult.overlapResult.error;
          const errorMessage = String(primaryError?.message || '');
          const missingSourceColumns = errorMessage.includes('source_type') || errorMessage.includes('source_id');

          if (!missingSourceColumns) throw primaryError;

          const fallbackRowsResult = await fetchRowsBySelect(baseSelect);
          if (fallbackRowsResult.withinDayResult.error) throw fallbackRowsResult.withinDayResult.error;
          if (fallbackRowsResult.overlapResult.error) throw fallbackRowsResult.overlapResult.error;

          rowsWithinDay = fallbackRowsResult.withinDayResult.data || [];
          rowsOverlappingDayStart = fallbackRowsResult.overlapResult.data || [];
        } else {
          rowsWithinDay = extendedRowsResult.withinDayResult.data || [];
          rowsOverlappingDayStart = extendedRowsResult.overlapResult.data || [];
        }

        const mergedRowsByKey = new Map<string, Record<string, any>>();
        [...rowsWithinDay, ...rowsOverlappingDayStart].forEach((row) => {
          const rowKey = typeof row.id === 'string' && row.id.length > 0
            ? row.id
            : `${row.start || 'no-start'}-${row.end || 'no-end'}`;
          mergedRowsByKey.set(rowKey, row);
        });

        const intervals = Array.from(mergedRowsByKey.values())
          .filter((row) => !(row.source_type === 'customer_sample' && row.source_id === sampleId))
          .map((row) => {
            const eventStart = row.start ? new Date(row.start) : null;
            if (!eventStart || Number.isNaN(eventStart.getTime())) {
              return null;
            }

            let eventEnd: Date;
            if (row.all_day) {
              eventEnd = dayEnd;
            } else if (row.end) {
              eventEnd = new Date(row.end);
              if (Number.isNaN(eventEnd.getTime())) {
                eventEnd = new Date(eventStart.getTime() + (DEFAULT_DURATION_MINUTES * 60 * 1000));
              }
            } else {
              eventEnd = new Date(eventStart.getTime() + (DEFAULT_DURATION_MINUTES * 60 * 1000));
            }

            const clampedStartMs = Math.max(eventStart.getTime(), dayStart.getTime());
            const clampedEndMs = Math.min(eventEnd.getTime(), dayEnd.getTime());
            if (clampedEndMs <= clampedStartMs) {
              return null;
            }

            const startMinutes = Math.max(0, Math.floor((clampedStartMs - dayStart.getTime()) / 60000));
            const endMinutes = Math.min(24 * 60, Math.ceil((clampedEndMs - dayStart.getTime()) / 60000));
            if (endMinutes <= startMinutes) {
              return null;
            }

            return { startMinutes, endMinutes };
          })
          .filter((interval): interval is { startMinutes: number; endMinutes: number } => Boolean(interval));

        if (!cancelled) {
          setBusyIntervals(intervals);
        }
      } catch (loadBusyError) {
        console.error('Error loading busy intervals for appointment suggestions:', loadBusyError);
        if (!cancelled) {
          setBusyIntervals([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingBusyIntervals(false);
        }
      }
    };

    loadBusyIntervalsForAssignedUser();

    return () => {
      cancelled = true;
    };
  }, [assignedAppointmentDate, assignedUserId, organizationId, sampleId, show, supabase]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      if (!user) throw new Error('Benutzer nicht angemeldet');

      if (!appointmentDate) {
        throw new Error('Bitte ein Einbestellungsdatum auswählen.');
      }
      if (!appointmentTime) {
        throw new Error('Bitte eine Einbestellungszeit auswählen.');
      }

      if (requiresManualAppointmentSlot) {
        if (!assignedAppointmentDate) {
          throw new Error('Bitte einen Termin-Tag auswählen.');
        }
        if (!assignedAppointmentTime) {
          throw new Error('Bitte eine Termin-Uhrzeit auswählen.');
        }
      }

      const needsAppointmentDuration = requiresManualAppointmentSlot || isEmailOrder || isSmsOrder || isWhatsappOrder;
      let parsedDuration: number | null = null;
      if (needsAppointmentDuration) {
        parsedDuration = Number.parseInt(assignedAppointmentDurationMinutes, 10);
        if (!Number.isFinite(parsedDuration) || parsedDuration <= 0) {
          throw new Error('Bitte eine gültige Termindauer in Minuten angeben.');
        }
      }

      if (isEmailOrder) {
        if (!emailOrderAvailable) {
          throw new Error('E-Mail-Einbestellung ist für diesen Kunden aktuell nicht verfügbar.');
        }
        if (!assignedUserId) {
          throw new Error('Für Einbestellung via E-Mail muss ein Benutzer zugewiesen sein.');
        }
        if (!customerEmail.trim()) {
          throw new Error('Bitte eine E-Mail-Adresse für die Einbestellung hinterlegen.');
        }
        if (!emailTemplateSubject.trim() || !emailTemplateContent.trim()) {
          throw new Error('Bitte hinterlegen Sie eine E-Mail-Einbestellungs-Vorlage in den E-Mail-Vorlagen.');
        }
        if (!organizationSlug) {
          throw new Error('Die Organisation hat keinen gültigen Slug für den Bestätigungslink.');
        }
        if (!assignedAppointmentDate) {
          throw new Error('Bitte einen Termin-Tag für die E-Mail-Einbestellung auswählen.');
        }
      }

      if (isSmsOrder) {
        if (!smsOrderAvailable) {
          throw new Error('SMS-Einbestellung ist für diesen Kunden aktuell nicht verfügbar.');
        }
        if (!assignedUserId) {
          throw new Error('Für Einbestellung via SMS muss ein Benutzer zugewiesen sein.');
        }
        if (!selectedSmsPhoneNumber) {
          throw new Error('Bitte eine Telefonnummer für die SMS-Einbestellung auswählen.');
        }
        if (!organizationSlug) {
          throw new Error('Die Organisation hat keinen gültigen Slug für den Bestätigungslink.');
        }
      }

      if (isWhatsappOrder) {
        if (!whatsappOptionVisible) {
          throw new Error('WhatsApp-Einbestellung ist für diesen Kunden aktuell nicht verfügbar.');
        }
        if (!whatsappHasPhoneNumbers) {
          throw new Error('Bitte eine Telefonnummer für die WhatsApp-Einbestellung hinterlegen.');
        }
        if (!whatsappHasTemplates) {
          throw new Error('Bitte zuerst eine WhatsApp-Vorlage in den Vorlagen anlegen.');
        }
        if (!selectedWhatsappPhoneNumber) {
          throw new Error('Bitte eine Telefonnummer für die WhatsApp-Einbestellung auswählen.');
        }
        if (!selectedWhatsappTemplateId || !selectedWhatsappTemplate) {
          throw new Error('Bitte eine WhatsApp-Vorlage für die Einbestellung auswählen.');
        }
        if (!selectedWhatsappTemplate.content?.trim()) {
          throw new Error('Die ausgewählte WhatsApp-Vorlage enthält keinen Nachrichtentext.');
        }
        if (!organizationSlug) {
          throw new Error('Die Organisation hat keinen gültigen Slug für den Bestätigungslink.');
        }
        if (!assignedAppointmentDate) {
          throw new Error('Bitte einen Termin-Tag für die WhatsApp-Einbestellung auswählen.');
        }
      }

      const nowIso = new Date().toISOString();
      const normalizedSmsPhoneNumber = isSmsOrder
        ? normalizeGermanSmsPhoneNumber(selectedSmsPhoneNumber)
        : '';
      const normalizedWhatsappPhoneNumber = isWhatsappOrder
        ? normalizeGermanSmsPhoneNumber(selectedWhatsappPhoneNumber)
        : '';
      const normalizedCustomerEmail = customerEmail.trim().toLowerCase();

      let emailOrderToken: string | null = null;
      let emailSubjectToStore: string | null = null;
      let emailSubjectToSend: string | null = null;
      let emailMessageToStore: string | null = null;
      let emailMessageToSend: string | null = null;
      let emailHtmlMessageToStore: string | null = null;
      let emailHtmlMessageToSend: string | null = null;
      let emailSignatureToSend: string | null = null;
      let emailConfirmationLinkPath: string | null = null;
      let emailConfirmationLink: string | null = null;
      let smsOrderToken: string | null = null;
      let smsMessageToSend: string | null = null;
      let whatsappOrderToken: string | null = null;
      let whatsappTemplateName: string | null = null;
      let whatsappMessageToStore: string | null = null;
      let whatsappMessageToSend: string | null = null;
      let whatsappConfirmationLinkPath: string | null = null;
      let whatsappConfirmationLink: string | null = null;
      // Einbestellungsdatum/-zeit bleibt der Kontaktzeitpunkt.
      // Der eigentliche Termin (assigned_appointment_*) wird separat geführt.
      const assignedAppointmentDateForUpdate =
        requiresManualAppointmentSlot || isEmailOrder || isSmsOrder || isWhatsappOrder ? assignedAppointmentDate : null;
      const assignedAppointmentTimeForUpdate = requiresManualAppointmentSlot ? assignedAppointmentTime : null;
      const formattedAppointmentDate = formatAppointmentDateForTemplate(assignedAppointmentDate);
      // Für E-Mail/SMS/WhatsApp gibt es vor der Bestätigung keinen finalen Slot, daher nutzen wir einen stabilen Fallbacktext.
      const appointmentTimeForTemplate = requiresManualAppointmentSlot && assignedAppointmentTime
        ? assignedAppointmentTime
        : 'nach Ihrer Auswahl';
      const appointmentLocationForTemplate = organizationAddress || organizationName || 'wird nachgereicht';

      if (isEmailOrder) {
        emailOrderToken = crypto.randomUUID();
        emailSubjectToStore = emailTemplateSubject?.trim() || DEFAULT_EMAIL_ORDER_SUBJECT;
        emailMessageToStore = emailTemplateContent?.trim() || DEFAULT_EMAIL_ORDER_BODY;
        emailHtmlMessageToStore = emailTemplateHtmlContent?.trim()
          || convertPlainTextToHtml(emailMessageToStore);
        emailConfirmationLinkPath = organizationSlug
          ? `/einbestellung/${organizationSlug}/${emailOrderToken}`
          : null;
        emailConfirmationLink = emailConfirmationLinkPath
          ? `${window.location.origin}${emailConfirmationLinkPath}`
          : null;
        const emailTemplateValues = {
          organization_name: organizationName || 'Ihre Organisation',
          organization_address: organizationAddress,
          customer_name: customerName || 'Kundin/Kunde',
          appointment_date: formattedAppointmentDate,
          appointment_time: appointmentTimeForTemplate,
          appointment_location: appointmentLocationForTemplate,
          appointment_confirmation_link: emailConfirmationLink || APPOINTMENT_CONFIRMATION_PLACEHOLDER,
        };
        emailSubjectToSend = applyTemplatePlaceholders(emailSubjectToStore, emailTemplateValues);
        emailMessageToSend = applyTemplatePlaceholders(emailMessageToStore, emailTemplateValues);
        emailHtmlMessageToSend = applyTemplatePlaceholders(emailHtmlMessageToStore, emailTemplateValues);
        if (emailTemplateSignature?.trim()) {
          emailSignatureToSend = applyTemplatePlaceholders(emailTemplateSignature.trim(), emailTemplateValues);
          emailMessageToSend = `${emailMessageToSend}\n\n${emailSignatureToSend}`;
          emailHtmlMessageToSend = `${emailHtmlMessageToSend}<br /><br />${convertPlainTextToHtml(emailSignatureToSend)}`;
        }
      }

      if (isSmsOrder) {
        smsOrderToken = crypto.randomUUID();
        const confirmationLink = `${window.location.origin}/einbestellung/${organizationSlug}/${smsOrderToken}`;

        // Bei SMS bestätigt der Kunde den Zeitslot selbst über den Link.
        smsMessageToSend = applyTemplatePlaceholders(
          smsTemplateContent?.trim() || DEFAULT_SMS_TEMPLATE,
          {
            organization_name: organizationName || 'Ihre Organisation',
            organization_address: organizationAddress,
            customer_name: customerName || 'Kundin/Kunde',
            appointment_date: formattedAppointmentDate,
            appointment_time: appointmentTimeForTemplate,
            appointment_location: appointmentLocationForTemplate,
            appointment_confirmation_link: confirmationLink,
          },
        );
      }

      if (isWhatsappOrder && selectedWhatsappTemplate) {
        // UUIDv4 kommt aus Web Crypto und bietet ausreichend Entropie für Einbestellungslinks.
        whatsappOrderToken = crypto.randomUUID();
        whatsappTemplateName = selectedWhatsappTemplate.internal_name;
        // Der Vorlagentext bleibt als Rohtext erhalten, damit der Platzhalter später ersetzt werden kann.
        whatsappMessageToStore = selectedWhatsappTemplate.content?.trim() || null;
        whatsappConfirmationLinkPath = organizationSlug
          ? `/einbestellung/${organizationSlug}/${whatsappOrderToken}`
          : null;
        whatsappConfirmationLink = whatsappConfirmationLinkPath
          ? `${window.location.origin}${whatsappConfirmationLinkPath}`
          : null;
        whatsappMessageToSend = whatsappMessageToStore
          ? applyTemplatePlaceholders(
            whatsappMessageToStore,
            {
              organization_name: organizationName || 'Ihre Organisation',
              organization_address: organizationAddress,
              customer_name: customerName || 'Kundin/Kunde',
              appointment_date: formattedAppointmentDate,
              appointment_time: appointmentTimeForTemplate,
              appointment_location: appointmentLocationForTemplate,
              appointment_confirmation_link: whatsappConfirmationLink || APPOINTMENT_CONFIRMATION_PLACEHOLDER,
            },
          )
          : null;
      }

      const baseUpdatePayload = {
        status: 'appointed',
        appointment_date: appointmentDate,
        appointment_time: appointmentTime,
        appointed_at: nowIso,
        appointed_by: user.full_name || user.id || 'Unbekannt',
        appointed_by_uuid: user.id,
        assigned_user_id: assignedUserId || null,
      };

      const extendedUpdatePayload = {
        ...baseUpdatePayload,
        assigned_appointment_date: assignedAppointmentDateForUpdate,
        assigned_appointment_time: assignedAppointmentTimeForUpdate,
        assigned_appointment_duration_minutes: needsAppointmentDuration ? parsedDuration : null,
        email_order_enabled: isEmailOrder,
        email_order_email: isEmailOrder ? normalizedCustomerEmail : null,
        email_order_token: isEmailOrder ? emailOrderToken : null,
        email_order_sent_at: isEmailOrder ? nowIso : null,
        email_order_confirmed_at: null,
        email_order_subject: isEmailOrder ? emailSubjectToStore : null,
        email_order_message: isEmailOrder ? emailMessageToStore : null,
        sms_order_enabled: isSmsOrder,
        sms_order_token: isSmsOrder ? smsOrderToken : null,
        sms_order_phone_number: isSmsOrder ? normalizedSmsPhoneNumber : null,
        sms_order_sent_at: isSmsOrder ? nowIso : null,
        sms_order_confirmed_at: null,
        sms_order_message: isSmsOrder ? smsMessageToSend : null,
        whatsapp_order_enabled: isWhatsappOrder,
        whatsapp_order_phone_number: isWhatsappOrder ? normalizedWhatsappPhoneNumber : null,
        whatsapp_order_token: isWhatsappOrder ? whatsappOrderToken : null,
        whatsapp_order_confirmed_at: null,
        whatsapp_order_template_id: isWhatsappOrder ? selectedWhatsappTemplateId : null,
        whatsapp_order_template_name: isWhatsappOrder ? whatsappTemplateName : null,
        whatsapp_order_message: isWhatsappOrder ? whatsappMessageToStore : null,
      };

      let { error: updateError } = await supabase
        .from('customer_samples')
        .update(extendedUpdatePayload)
        .eq('id', sampleId);

      if (updateError) {
        const errorMessage = String(updateError.message || '');
        const missingAssignedColumns =
          errorMessage.includes('assigned_appointment_date')
          || errorMessage.includes('assigned_appointment_time')
          || errorMessage.includes('assigned_appointment_duration_minutes');
        const missingEmailColumns =
          errorMessage.includes('email_order_enabled')
          || errorMessage.includes('email_order_email')
          || errorMessage.includes('email_order_token')
          || errorMessage.includes('email_order_sent_at')
          || errorMessage.includes('email_order_confirmed_at')
          || errorMessage.includes('email_order_subject')
          || errorMessage.includes('email_order_message');
        const missingSmsColumns =
          errorMessage.includes('sms_order_enabled')
          || errorMessage.includes('sms_order_token')
          || errorMessage.includes('sms_order_phone_number')
          || errorMessage.includes('sms_order_sent_at')
          || errorMessage.includes('sms_order_confirmed_at')
          || errorMessage.includes('sms_order_message');
        const missingWhatsappColumns =
          errorMessage.includes('whatsapp_order_enabled')
          || errorMessage.includes('whatsapp_order_phone_number')
          || errorMessage.includes('whatsapp_order_token')
          || errorMessage.includes('whatsapp_order_confirmed_at')
          || errorMessage.includes('whatsapp_order_template_id')
          || errorMessage.includes('whatsapp_order_template_name')
          || errorMessage.includes('whatsapp_order_message');

        if (isSmsOrder && (missingAssignedColumns || missingSmsColumns)) {
          throw new Error(
            'Die SMS-Einbestellung benötigt aktuelle Datenbank-Migrationen. Bitte Migrationen anwenden und erneut versuchen.',
          );
        }

        if (isEmailOrder && (missingAssignedColumns || missingEmailColumns)) {
          throw new Error(
            'Die E-Mail-Einbestellung benötigt aktuelle Datenbank-Migrationen. Bitte Migrationen anwenden und erneut versuchen.',
          );
        }

        if (isWhatsappOrder && (missingAssignedColumns || missingWhatsappColumns)) {
          throw new Error(
            'Die WhatsApp-Einbestellung benötigt aktuelle Datenbank-Migrationen. Bitte Migrationen anwenden und erneut versuchen.',
          );
        }

        if (!missingAssignedColumns && !missingEmailColumns && !missingSmsColumns && !missingWhatsappColumns) {
          throw updateError;
        }

        const fallbackUpdate = await supabase
          .from('customer_samples')
          .update(baseUpdatePayload)
          .eq('id', sampleId);

        if (fallbackUpdate.error) throw fallbackUpdate.error;
      }

      if (isSmsOrder && smsMessageToSend && normalizedSmsPhoneNumber) {
        const smsPayload = {
          sample_id: sampleId,
          organization_id: organizationId || null,
          organization_name: organizationName || null,
          phone_number: normalizedSmsPhoneNumber,
          message_text: smsMessageToSend,
        };

        const typedSmsResult = await callProtectedEdgeFunction<{
          success?: boolean;
          error?: string;
          message?: string;
          detail?: string;
          details?: string;
        }>(
          'sample-order-sms-notify',
          smsPayload,
          'SMS konnte nicht versendet werden.',
        );

        if (typedSmsResult?.success === false) {
          const responseMessage = typedSmsResult?.error
            || typedSmsResult?.message
            || typedSmsResult?.detail
            || typedSmsResult?.details
            || 'SMS konnte nicht versendet werden.';

          // Best effort: Wenn SMS-Versand scheitert, SMS-Markierungen im Datensatz wieder entfernen.
          await supabase
            .from('customer_samples')
            .update({
              sms_order_enabled: false,
              sms_order_token: null,
              sms_order_phone_number: null,
              sms_order_sent_at: null,
              sms_order_confirmed_at: null,
              sms_order_message: null,
            })
            .eq('id', sampleId);

          throw new Error(responseMessage || 'SMS konnte nicht versendet werden.');
        }
      }

      if (isEmailOrder && emailMessageToStore && emailSubjectToStore && normalizedCustomerEmail) {
        const resolvedEmailPlainMessage = emailMessageToSend || emailMessageToStore;
        const resolvedEmailHtmlMessage = emailHtmlMessageToSend || null;
        // message_text bleibt für die bestehende Nutzlast kompatibel und nutzt HTML, wenn vorhanden.
        const resolvedEmailPrimaryMessage = resolvedEmailHtmlMessage || resolvedEmailPlainMessage;
        const templateEmailPrimaryMessage = emailHtmlMessageToStore || emailMessageToStore;
        const emailWebhookPayload = {
          event: 'sample_order_email_requested',
          channel: 'email',
          sample_id: sampleId,
          order_id: sampleId,
          organization_id: organizationId || null,
          organization_slug: organizationSlug || null,
          organization_name: organizationName || null,
          customer_name: customerName || null,
          customer_email: normalizedCustomerEmail,
          subject: emailSubjectToSend || emailSubjectToStore,
          subject_template: emailSubjectToStore,
          subject_resolved: emailSubjectToSend || emailSubjectToStore,
          message_text: resolvedEmailPrimaryMessage,
          message_text_template: templateEmailPrimaryMessage,
          message_text_resolved: resolvedEmailPrimaryMessage,
          message_text_plain: resolvedEmailPlainMessage,
          message_text_plain_template: emailMessageToStore,
          message_text_plain_resolved: resolvedEmailPlainMessage,
          message_html: resolvedEmailHtmlMessage,
          message_html_template: emailHtmlMessageToStore,
          message_html_resolved: resolvedEmailHtmlMessage,
          message_signature: emailSignatureToSend || null,
          message_uses_confirmation_placeholder: Boolean(
            emailMessageToStore.includes(APPOINTMENT_CONFIRMATION_PLACEHOLDER)
            || emailHtmlMessageToStore?.includes(APPOINTMENT_CONFIRMATION_PLACEHOLDER)
            || emailSubjectToStore.includes(APPOINTMENT_CONFIRMATION_PLACEHOLDER),
          ),
          placeholder_confirmation_link: APPOINTMENT_CONFIRMATION_PLACEHOLDER,
          confirmation_link_token: emailOrderToken,
          confirmation_link_path: emailConfirmationLinkPath,
          confirmation_link: emailConfirmationLink,
          assigned_user_id: assignedUserId || null,
          order_date: appointmentDate || null,
          order_time: appointmentTime || null,
          appointment_date: assignedAppointmentDateForUpdate,
          appointment_time: assignedAppointmentTimeForUpdate,
          appointment_duration_minutes: needsAppointmentDuration ? parsedDuration : null,
          requested_at: nowIso,
        };

        const rollbackEmailOrderState = async () => {
          await supabase
            .from('customer_samples')
            .update({
              email_order_enabled: false,
              email_order_email: null,
              email_order_token: null,
              email_order_sent_at: null,
              email_order_confirmed_at: null,
              email_order_subject: null,
              email_order_message: null,
            })
            .eq('id', sampleId);
        };

        try {
          const emailResult = await callProtectedEdgeFunction<{ success?: boolean; error?: string }>(
            'sample-order-email-notify',
            emailWebhookPayload,
            'E-Mail konnte nicht an den Versanddienst übergeben werden.',
          );
          if (emailResult?.success === false) {
            throw new Error(emailResult.error || 'E-Mail konnte nicht an den Versanddienst übergeben werden.');
          }
        } catch (emailError) {
          await rollbackEmailOrderState();
          throw emailError;
        }
      }

      if (isWhatsappOrder && whatsappMessageToStore && normalizedWhatsappPhoneNumber) {
        const whatsappWebhookPayload = {
          event: 'sample_order_whatsapp_requested',
          channel: 'whatsapp',
          sample_id: sampleId,
          order_id: sampleId,
          organization_id: organizationId || null,
          organization_slug: organizationSlug || null,
          organization_name: organizationName || null,
          customer_name: customerName || null,
          phone_number: normalizedWhatsappPhoneNumber,
          // Versandtext enthält bereits den aufgelösten Bestätigungslink.
          message_text: whatsappMessageToSend || whatsappMessageToStore,
          message_text_template: whatsappMessageToStore,
          message_text_resolved: whatsappMessageToSend || whatsappMessageToStore,
          message_uses_confirmation_placeholder: Boolean(
            whatsappMessageToStore?.includes(APPOINTMENT_CONFIRMATION_PLACEHOLDER),
          ),
          message_template_id: selectedWhatsappTemplateId || null,
          message_template_name: whatsappTemplateName || null,
          placeholder_confirmation_link: APPOINTMENT_CONFIRMATION_PLACEHOLDER,
          confirmation_link_token: whatsappOrderToken,
          confirmation_link_path: whatsappConfirmationLinkPath,
          confirmation_link: whatsappConfirmationLink,
          assigned_user_id: assignedUserId || null,
          order_date: appointmentDate || null,
          order_time: appointmentTime || null,
          appointment_date: assignedAppointmentDateForUpdate,
          appointment_time: assignedAppointmentTimeForUpdate,
          appointment_duration_minutes: needsAppointmentDuration ? parsedDuration : null,
          requested_at: nowIso,
        };

        const rollbackWhatsappOrderState = async () => {
          await supabase
            .from('customer_samples')
            .update({
              whatsapp_order_enabled: false,
              whatsapp_order_phone_number: null,
              whatsapp_order_token: null,
              whatsapp_order_confirmed_at: null,
              whatsapp_order_template_id: null,
              whatsapp_order_template_name: null,
              whatsapp_order_message: null,
            })
            .eq('id', sampleId);
        };

        const typedWhatsappResult = await callProtectedEdgeFunction<{
          success?: boolean;
          error?: string;
          message?: string;
          detail?: string;
          details?: string;
        }>(
          'sample-order-whatsapp-notify',
          whatsappWebhookPayload,
          'WhatsApp konnte nicht über den Baileys-Dienst versendet werden.',
        );

        if (typedWhatsappResult?.success === false) {
          const responseMessage = typedWhatsappResult?.error
            || typedWhatsappResult?.message
            || typedWhatsappResult?.detail
            || typedWhatsappResult?.details
            || 'WhatsApp konnte nicht über den Baileys-Dienst versendet werden.';

          await rollbackWhatsappOrderState();
          throw new Error(responseMessage || 'WhatsApp konnte nicht über den Baileys-Dienst versendet werden.');
        }
      }
      
      onAppointmentScheduled();
      onClose();
    } catch (err: any) {
      console.error('Error scheduling appointment:', err);
      setError(err.message || 'Fehler beim Speichern des Termins');
    } finally {
      setLoading(false);
    }
  };
  
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-2 sm:items-center sm:p-4">
      {/* Das Modal bleibt auf kleinen Displays intern scrollbar und nutzt am Desktop mehr Breite. */}
      <div className="w-full max-w-[95vw] lg:max-w-7xl max-h-[calc(100vh-1rem)] overflow-y-auto rounded-lg bg-white p-4 shadow-xl dark:bg-gray-800 sm:max-h-[90vh] sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <CalendarClock className="h-5 w-5 mr-2 text-blue-500" />
            Termin vereinbaren
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-md text-sm flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg mb-2">
            <div className="flex items-center text-sm text-gray-700 dark:text-gray-300 mt-1">
              <User className="h-4 w-4 mr-2 text-blue-500" />
              <span>Einbestellt von: {user?.full_name || user?.id || 'Unbekannt'}</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Einbestellungsart
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="radio"
                  name="orderChannel"
                  value="phone"
                  checked={orderChannel === 'phone'}
                  onChange={(event) => setOrderChannel(event.target.value as OrderChannel)}
                  className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <Phone className="h-4 w-4 text-gray-500" />
                <span>Per Telefon einbestellt</span>
              </label>
              {/* Kanal-Buttons nur anzeigen, wenn der Kanal aktuell wirklich nutzbar ist. */}
              {emailOrderAvailable && (
                <label className="flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="radio"
                    name="orderChannel"
                    value="email"
                    checked={orderChannel === 'email'}
                    onChange={(event) => setOrderChannel(event.target.value as OrderChannel)}
                    className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span>Per E-Mail einbestellt</span>
                </label>
              )}
              {whatsappOptionVisible && (
                <label className="flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="radio"
                    name="orderChannel"
                    value="whatsapp"
                    checked={orderChannel === 'whatsapp'}
                    onChange={(event) => setOrderChannel(event.target.value as OrderChannel)}
                    disabled={!whatsappOrderAvailable}
                    className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed"
                  />
                  <MessageCircle className="h-4 w-4 text-gray-500" />
                  <span>Einbestellen via WhatsApp</span>
                </label>
              )}
              {smsOrderAvailable && (
                <label className="flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="radio"
                    name="orderChannel"
                    value="sms"
                    checked={orderChannel === 'sms'}
                    onChange={(event) => setOrderChannel(event.target.value as OrderChannel)}
                    className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <MessageSquare className="h-4 w-4 text-gray-500" />
                  <span>Per SMS einbestellt</span>
                </label>
              )}
            </div>
            {!whatsappOrderAvailable && whatsappOptionVisible && whatsappOptionHint && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{whatsappOptionHint}</p>
            )}
            {!emailOrderAvailable && emailTemplateHint && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{emailTemplateHint}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <CalendarClock className="h-4 w-4 mr-1 text-gray-400" />
              Einbestellungsdatum (Tag der Kontaktaufnahme)
            </label>
            <input
              type="date"
              value={appointmentDate}
              onChange={(e) => setAppointmentDate(e.target.value)}
              required
              className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Clock className="h-4 w-4 mr-1 text-gray-400" />
              Einbestellungszeit (Uhrzeit der Einbestellung)
            </label>
            <input
              type="time"
              value={appointmentTime}
              onChange={(e) => setAppointmentTime(e.target.value)}
              required
              className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <User className="h-4 w-4 mr-1 text-gray-400" />
              Benutzerzuweisung
            </label>
            <select
              value={assignedUserId}
              onChange={(e) => setAssignedUserId(e.target.value)}
              disabled={loadingUsers}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
            >
              <option value="">Nicht zugewiesen</option>
              {organizationUsers.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.full_name?.trim() || member.email || member.user_id}
                </option>
              ))}
            </select>
          </div>

          {requiresManualAppointmentSlot ? (
            <div className="space-y-4 rounded-md border border-primary-100 dark:border-primary-900 bg-primary-50/50 dark:bg-primary-900/20 p-3">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                  <Phone className="h-4 w-4 mr-1 text-gray-400" />
                  Telefonnummern des Kunden
                </p>
                {customerPhones.length > 0 ? (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {customerPhones.map((phone) => (
                      <div
                        key={phone.id}
                        className="rounded-md border border-gray-200 bg-white/70 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200"
                      >
                        <p className="font-medium">{phone.phone_number}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{phone.type || 'ohne Typ'}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Ohne hinterlegte Telefonnummer bleibt der Telefonkanal möglich, aber die UI weist auf das fehlende Ziel hin.
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                    Für diesen Kunden ist keine Telefonnummer hinterlegt.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <CalendarClock className="h-4 w-4 mr-1 text-gray-400" />
                    Termin-Tag (Tag, an dem der Kunde kommt)
                  </label>
                  <input
                    type="date"
                    value={assignedAppointmentDate}
                    onChange={(event) => setAssignedAppointmentDate(event.target.value)}
                    required={requiresManualAppointmentSlot}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-gray-400" />
                    Termin-Uhrzeit (Uhrzeit, an der der Kunde kommt)
                  </label>
                  <input
                    type="time"
                    value={assignedAppointmentTime}
                    onChange={(event) => setAssignedAppointmentTime(event.target.value)}
                    required={requiresManualAppointmentSlot}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-gray-400" />
                    Termindauer (Minuten)
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={assignedAppointmentDurationMinutes}
                    onChange={(event) => setAssignedAppointmentDurationMinutes(event.target.value)}
                    required={requiresManualAppointmentSlot}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  />
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Terminvorschläge (15-Minuten-Schritte)
                </p>
                {!assignedUserId ? (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                    Bitte zuerst eine Benutzerzuweisung auswählen, damit belegte Termine berücksichtigt werden.
                  </p>
                ) : loadingBusyIntervals ? (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Belegte Termine des ausgewählten Benutzers werden geladen.
                  </p>
                ) : phoneTimeSuggestions.length > 0 ? (
                  <>
                    {/* Auf Desktop werden mehr Zeitslots pro Zeile angezeigt, damit weniger gescrollt werden muss. */}
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                      {phoneTimeSuggestions.map((slotTime) => (
                        <button
                          key={slotTime}
                          type="button"
                          onClick={() => setAssignedAppointmentTime(slotTime)}
                          className={`rounded-md border px-2 py-1.5 text-sm ${
                            assignedAppointmentTime === slotTime
                              ? 'border-primary-600 bg-primary-50 text-primary-700 dark:border-primary-400 dark:bg-primary-900/30 dark:text-primary-200'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
                          }`}
                        >
                          {slotTime}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                    {openingHoursForSelectedDate?.open
                      ? 'Für den gewählten Tag konnten keine Terminvorschläge erzeugt werden.'
                      : 'Für den gewählten Tag sind laut Öffnungszeiten keine Termine verfügbar.'}
                  </p>
                )}
                {openingHoursForSelectedDate?.open && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Öffnungszeiten am gewählten Tag: {openingHoursForSelectedDate.start} - {openingHoursForSelectedDate.end}
                  </p>
                )}
              </div>
            </div>
          ) : !isSmsOrder && !isWhatsappOrder && !isEmailOrder ? (
            <div className="space-y-2">
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Ohne "Per Telefon einbestellt" wird der Termin später nachgetragen.
              </p>
              {nonPhoneAvailabilityInfo && (
                <p className={nonPhoneAvailabilityInfo.className}>{nonPhoneAvailabilityInfo.text}</p>
              )}
            </div>
          ) : null}

          {isEmailOrder && (
            <div className="space-y-4 rounded-md border border-primary-100 dark:border-primary-900 bg-primary-50/50 dark:bg-primary-900/20 p-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <Mail className="h-4 w-4 mr-1 text-gray-400" />
                  E-Mail-Adresse für Einbestellung
                </label>
                <input
                  type="email"
                  value={customerEmail}
                  readOnly
                  className="w-full rounded-md border-gray-300 bg-gray-100 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-200"
                />
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">E-Mail-Vorlage</p>
                <div className="mt-2 rounded-md bg-white/70 p-3 text-xs text-gray-600 dark:bg-gray-800/70 dark:text-gray-300">
                  <p className="font-semibold">
                    Betreff: {emailTemplateSubject || DEFAULT_EMAIL_ORDER_SUBJECT}
                  </p>
                  <p className="mt-2 whitespace-pre-wrap">
                    {emailTemplateContent || DEFAULT_EMAIL_ORDER_BODY}
                  </p>
                  {emailTemplateSignature?.trim() && (
                    <p className="mt-2 whitespace-pre-wrap border-t border-gray-200 pt-2 dark:border-gray-600">
                      {emailTemplateSignature.trim()}
                    </p>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Diese Vorlage kann unter „Vorlagen → Email-Vorlagen“ (Typ „Einbestellung per E-Mail-Link“) angepasst werden.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <CalendarClock className="h-4 w-4 mr-1 text-gray-400" />
                    Termin-Tag (Tag, an dem der Kunde auswählt/kommt)
                  </label>
                  <input
                    type="date"
                    value={assignedAppointmentDate}
                    onChange={(event) => setAssignedAppointmentDate(event.target.value)}
                    required
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-gray-400" />
                    Dauer (Minuten)
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={assignedAppointmentDurationMinutes}
                    onChange={(event) => setAssignedAppointmentDurationMinutes(event.target.value)}
                    required
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  />
                </div>
              </div>
              {nonPhoneAvailabilityInfo && (
                <p className={nonPhoneAvailabilityInfo.className}>{nonPhoneAvailabilityInfo.text}</p>
              )}
            </div>
          )}

          {isSmsOrder && (
            <div className="space-y-4 rounded-md border border-primary-100 dark:border-primary-900 bg-primary-50/50 dark:bg-primary-900/20 p-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <MessageSquare className="h-4 w-4 mr-1 text-gray-400" />
                  Telefonnummer für SMS-Einbestellung
                </label>
                <select
                  value={selectedSmsPhoneNumber}
                  onChange={(event) => setSelectedSmsPhoneNumber(event.target.value)}
                  required
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                >
                  <option value="">Bitte Telefonnummer auswählen</option>
                  {customerPhones.map((phone) => (
                    <option key={phone.id} value={phone.phone_number}>
                      {phone.phone_number} ({phone.type || 'ohne Typ'})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {!requiresManualAppointmentSlot && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                      <CalendarClock className="h-4 w-4 mr-1 text-gray-400" />
                      Auswahltag für Kundenbestätigung
                    </label>
                    <input
                      type="date"
                      value={assignedAppointmentDate}
                      onChange={(event) => setAssignedAppointmentDate(event.target.value)}
                      required={!requiresManualAppointmentSlot}
                      className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-gray-400" />
                    Dauer (Minuten)
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={assignedAppointmentDurationMinutes}
                    onChange={(event) => setAssignedAppointmentDurationMinutes(event.target.value)}
                    required
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  />
                </div>
              </div>
              {nonPhoneAvailabilityInfo && (
                <p className={nonPhoneAvailabilityInfo.className}>{nonPhoneAvailabilityInfo.text}</p>
              )}
            </div>
          )}

          {isWhatsappOrder && (
            <div className="space-y-4 rounded-md border border-primary-100 dark:border-primary-900 bg-primary-50/50 dark:bg-primary-900/20 p-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <MessageCircle className="h-4 w-4 mr-1 text-gray-400" />
                  Telefonnummer für WhatsApp-Einbestellung
                </label>
                <select
                  value={selectedWhatsappPhoneNumber}
                  onChange={(event) => setSelectedWhatsappPhoneNumber(event.target.value)}
                  required
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                >
                  <option value="">Bitte Telefonnummer auswählen</option>
                  {customerPhones.map((phone) => (
                    <option key={phone.id} value={phone.phone_number}>
                      {phone.phone_number} ({phone.type || 'ohne Typ'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                  <MessageCircle className="h-4 w-4 mr-1 text-gray-400" />
                  WhatsApp-Vorlage
                </label>
                <select
                  value={selectedWhatsappTemplateId}
                  onChange={(event) => setSelectedWhatsappTemplateId(event.target.value)}
                  required
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                >
                  <option value="">Bitte Vorlage auswählen</option>
                  {whatsappTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.internal_name}
                    </option>
                  ))}
                </select>
                {selectedWhatsappTemplate?.content && (
                  <p className="mt-2 whitespace-pre-wrap rounded-md bg-white/70 p-2 text-xs text-gray-600 dark:bg-gray-800/70 dark:text-gray-300">
                    {selectedWhatsappTemplate.content}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <CalendarClock className="h-4 w-4 mr-1 text-gray-400" />
                    Termin-Tag (Tag, an dem der Kunde auswählt/kommt)
                  </label>
                  <input
                    type="date"
                    value={assignedAppointmentDate}
                    onChange={(event) => setAssignedAppointmentDate(event.target.value)}
                    required
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-gray-400" />
                    Dauer (Minuten)
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={assignedAppointmentDurationMinutes}
                    onChange={(event) => setAssignedAppointmentDurationMinutes(event.target.value)}
                    required
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  />
                </div>
              </div>
              {nonPhoneAvailabilityInfo && (
                <p className={nonPhoneAvailabilityInfo.className}>{nonPhoneAvailabilityInfo.text}</p>
              )}
            </div>
          )}

          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Einbestellung am {new Date(appointmentDate).toLocaleDateString('de-DE')} um {appointmentTime} Uhr
              über {selectedOrderChannelLabel}.
            </p>
            {requiresManualAppointmentSlot ? (
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Termin am {new Date(assignedAppointmentDate).toLocaleDateString('de-DE')} um {assignedAppointmentTime} Uhr.
              </p>
            ) : isEmailOrder ? (
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Für E-Mail wird der Termin-Tag am {new Date(assignedAppointmentDate).toLocaleDateString('de-DE')} vorbereitet.
              </p>
            ) : isSmsOrder ? (
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Für SMS wird der Termin-Tag am {new Date(assignedAppointmentDate).toLocaleDateString('de-DE')} vorbereitet.
              </p>
            ) : isWhatsappOrder ? (
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Für WhatsApp wird der Termin-Tag am {new Date(assignedAppointmentDate).toLocaleDateString('de-DE')} vorbereitet.
              </p>
            ) : (
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Termin-Tag und Termin-Uhrzeit werden später ergänzt.
              </p>
            )}
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Der Status der Probe wird auf "Einbestellt" gesetzt.
            </p>
            {isEmailOrder && (
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Für {customerName || 'den Kunden'} wird eine E-Mail mit Bestätigungslink an {customerEmail || 'die hinterlegte Adresse'} versendet.
              </p>
            )}
            {isSmsOrder && (
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Für {customerName || 'den Kunden'} wird ein SMS-Link zur Terminbestätigung am{' '}
                {new Date(assignedAppointmentDate).toLocaleDateString('de-DE')} versendet.
              </p>
            )}
            {isWhatsappOrder && (
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Für {customerName || 'den Kunden'} wird die WhatsApp-Vorlage "{selectedWhatsappTemplate?.internal_name || 'ohne Auswahl'}" an den Versand-Webhook übergeben.
              </p>
            )}
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Wird gespeichert...
                </>
              ) : (
                'Termin speichern'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentModal;







